import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, X, Sparkles, Copy, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Keypair } from '@solana/web3.js';
import bs58 from 'bs58';
import OnboardingCarousel from './OnboardingCarousel';

/**
 * Generate a REAL Solana keypair
 */
const generateSolanaWallet = () => {
  const keypair = Keypair.generate();
  const publicKey = keypair.publicKey.toBase58();
  const secretKey = bs58.encode(keypair.secretKey);
  
  return {
    publicKey,
    secretKey,
    shortId: `${publicKey.slice(0, 4)}...${publicKey.slice(-4)}`,
    createdAt: Date.now()
  };
};

// Save Key Modal - Shows the REAL Solana address and secret key
const SaveKeyModal = ({ wallet, onConfirm, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const handleCopySecretKey = () => {
    navigator.clipboard.writeText(wallet.secretKey);
    setCopied(true);
    toast.success('Secret key copied!');
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <motion.div
      className="fixed inset-0 z-[10000] bg-black flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-3 bg-red-500/20 hover:bg-red-500/40 rounded-full transition-colors z-10"
        data-testid="save-key-close-btn"
      >
        <X className="w-6 h-6 text-red-400" />
      </button>

      <motion.div
        className="bg-[#0d0d0d]/95 border-2 border-yellow-500/50 rounded-3xl p-6 max-w-md w-full backdrop-blur-xl"
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
      >
        {/* Success Badge */}
        <div className="flex justify-center mb-4">
          <div className="px-4 py-2 bg-green-500/20 border border-green-500/50 rounded-full">
            <span className="text-green-400 text-sm font-bold flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Wallet Generated!
            </span>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-center text-2xl font-black text-yellow-400 mb-2 flex items-center justify-center gap-2">
          <AlertTriangle className="w-6 h-6" />
          SAVE YOUR KEY!
        </h2>
        <p className="text-center text-white/60 text-sm mb-6">
          This is the ONLY way to recover your wallet. We cannot help if you lose it.
        </p>

        {/* Solana Address */}
        <div className="bg-black/50 rounded-xl p-4 border border-green-500/30 mb-4">
          <p className="text-green-400 text-xs mb-1 font-bold">YOUR SOLANA ADDRESS</p>
          <p className="text-white font-mono text-xs break-all">{wallet.publicKey}</p>
          <p className="text-white/40 text-[10px] mt-2">
            ✓ This is a REAL Solana address. You can receive SOL and tokens here.
          </p>
        </div>

        {/* Secret Key */}
        <div className="bg-black/50 rounded-xl p-4 border border-red-500/30 mb-4">
          <p className="text-red-400 text-xs mb-1 font-bold flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            SECRET KEY (NEVER SHARE!)
          </p>
          <p className="text-yellow-400 font-mono text-[10px] break-all mb-3">
            {wallet.secretKey.slice(0, 20)}...{wallet.secretKey.slice(-20)}
          </p>
          <button
            onClick={handleCopySecretKey}
            className={`w-full py-2 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all ${
              copied 
                ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                : 'bg-red-500/20 text-red-400 border border-red-500/50 hover:bg-red-500/30'
            }`}
          >
            {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? 'COPIED!' : 'COPY SECRET KEY'}
          </button>
        </div>

        {/* Warning */}
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 mb-4">
          <div className="text-yellow-400 text-xs font-bold mb-1">⚠️ WARNING:</div>
          <ul className="text-white/60 text-xs space-y-1">
            <li>• Write this key down on paper</li>
            <li>• Store it in a password manager</li>
            <li>• NEVER share it with anyone</li>
            <li>• Lost key = Lost funds forever</li>
          </ul>
        </div>

        {/* Checkbox */}
        <label className="flex items-start gap-3 mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="mt-1 w-5 h-5 rounded border-2 border-white/30 bg-transparent checked:bg-green-500"
            data-testid="save-key-checkbox"
          />
          <span className="text-white/80 text-sm">
            ✅ I have saved my Secret Key in a safe place
          </span>
        </label>

        {/* Continue Button */}
        <button
          onClick={onConfirm}
          disabled={!confirmed}
          className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
            confirmed
              ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg shadow-green-500/30'
              : 'bg-gray-700 text-gray-500 cursor-not-allowed'
          }`}
          data-testid="save-key-confirm-btn"
        >
          {confirmed ? 'CONTINUE TO GAME' : 'CHECK THE BOX ABOVE'}
        </button>
      </motion.div>
    </motion.div>
  );
};

const WelcomeOverlay = ({ onWalletCreated, onClose }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [wallet, setWallet] = useState(null);
  
  // Multi-step flow
  const [showSaveKeyModal, setShowSaveKeyModal] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // EMERGENCY FIX: Force close function that ALWAYS works
  const forceClose = () => {
    console.log('[WelcomeOverlay] Force closing...');
    localStorage.setItem('wallet_created', 'true');
    localStorage.setItem('onboarding_complete', 'true');
    localStorage.setItem('tutorial_completed', 'true');
    setShowSaveKeyModal(false);
    setShowOnboarding(false);
    onClose?.();
  };

  // Check if wallet already exists
  useEffect(() => {
    const existing = localStorage.getItem('solana_wallet');
    const onboardingDone = localStorage.getItem('onboarding_complete');
    
    if (existing) {
      try {
        const walletData = JSON.parse(existing);
        // Validate it's a proper Solana wallet (Base58 public key)
        if (walletData.publicKey && walletData.publicKey.length >= 32) {
          setWallet(walletData);
          
          if (!onboardingDone) {
            setShowOnboarding(true);
          } else {
            onClose?.();
          }
        }
      } catch (e) {
        console.error('Invalid wallet data:', e);
      }
    }
  }, [onClose]);

  const handleCreateWallet = async () => {
    setIsGenerating(true);
    
    // Simulate generation delay for UX
    await new Promise(r => setTimeout(r, 1200));
    
    try {
      const newWallet = generateSolanaWallet();
      setWallet(newWallet);
      setIsGenerating(false);
      
      // Show the Save Key Modal
      setShowSaveKeyModal(true);
      
      toast.success('Wallet Generated!', {
        description: `Address: ${newWallet.shortId}`
      });
    } catch (e) {
      console.error('Wallet generation failed:', e);
      toast.error('Failed to generate wallet');
      setIsGenerating(false);
    }
  };

  const handleKeyConfirmed = () => {
    // NOW save wallet to localStorage
    localStorage.setItem('solana_wallet', JSON.stringify(wallet));
    localStorage.setItem('player_id', wallet.publicKey);
    localStorage.setItem('wallet_created', 'true');
    
    // Remove old format if exists
    localStorage.removeItem('burner_wallet');
    
    // Close Save Key Modal, show Onboarding Carousel
    setShowSaveKeyModal(false);
    setShowOnboarding(true);
  };

  const handleOnboardingComplete = () => {
    localStorage.setItem('onboarding_complete', 'true');
    localStorage.setItem('tutorial_completed', 'true');
    setShowOnboarding(false);
    
    // Notify parent
    onWalletCreated?.({ 
      publicKey: wallet.publicKey, 
      secretKey: wallet.secretKey 
    });
    onClose?.();
  };

  // If showing Save Key Modal
  if (showSaveKeyModal && wallet) {
    return (
      <SaveKeyModal
        wallet={wallet}
        onConfirm={handleKeyConfirmed}
        onClose={() => {
          setShowSaveKeyModal(false);
          setWallet(null);
        }}
      />
    );
  }

  // If showing Onboarding Carousel
  if (showOnboarding) {
    return (
      <OnboardingCarousel
        isOpen={showOnboarding}
        onComplete={handleOnboardingComplete}
      />
    );
  }

  return (
    <motion.div
      className="fixed inset-0 z-[10000] bg-black flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="welcome-overlay"
    >
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-green-600/20 rounded-full blur-3xl" />
      </div>

      {/* EMERGENCY FIX: Close Button with z-index 9999 - ALWAYS CLICKABLE */}
      <button
        onClick={forceClose}
        className="absolute top-4 right-4 p-4 bg-red-500/30 hover:bg-red-500/60 rounded-full transition-colors cursor-pointer"
        style={{ zIndex: 99999 }}
        data-testid="welcome-close-btn"
      >
        <X className="w-8 h-8 text-white" />
      </button>

      <div className="relative max-w-lg w-full text-center">
        {/* Logo / Title */}
        <motion.div
          initial={{ y: -30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <h1 
            className="text-4xl sm:text-6xl font-black mb-2"
            style={{
              background: 'linear-gradient(135deg, #FF00FF 0%, #00FF99 50%, #FFFF00 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            SLAP BATTLES
          </h1>
          <p className="text-white/60 text-lg mb-8">THE MIDNIGHT ARCADE</p>
        </motion.div>

        {/* Main Card */}
        <motion.div
          className="bg-[#0d0d0d]/90 border-2 border-green-500/50 rounded-3xl p-6 sm:p-8 backdrop-blur-xl"
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          {/* Icon */}
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
            <Zap className="w-10 h-10 text-white" />
          </div>

          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
            WELCOME, SLAPPER!
          </h2>
          <p className="text-white/60 mb-4">
            Create a Solana wallet to start playing.<br/>
            No downloads. No signups. Just play.
          </p>
          
          {/* Highlight - Real Solana Wallet */}
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 mb-6">
            <p className="text-green-400 text-sm font-bold">
              ✓ Real Solana Wallet
            </p>
            <p className="text-white/50 text-xs">
              Receive SOL and tokens directly to your address
            </p>
          </div>

          {/* PRIMARY BUTTON */}
          <motion.button
            onClick={handleCreateWallet}
            disabled={isGenerating}
            className="w-full py-5 sm:py-6 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl font-black text-xl sm:text-2xl text-white shadow-2xl shadow-green-500/40 disabled:opacity-50 flex items-center justify-center gap-3"
            animate={!isGenerating ? {
              boxShadow: [
                '0 0 30px rgba(34,197,94,0.4)',
                '0 0 60px rgba(34,197,94,0.6)',
                '0 0 30px rgba(34,197,94,0.4)',
              ]
            } : {}}
            transition={{ duration: 1.5, repeat: Infinity }}
            whileTap={{ scale: 0.98 }}
            data-testid="create-player-wallet-btn"
          >
            {isGenerating ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <Sparkles className="w-7 h-7" />
                </motion.div>
                GENERATING...
              </>
            ) : (
              <>
                <Zap className="w-7 h-7" />
                CREATE SOLANA WALLET
              </>
            )}
          </motion.button>

          {/* Info text */}
          <p className="text-white/30 text-xs mt-6">
            Your wallet is stored locally in your browser.
            Make sure to save your secret key!
          </p>
        </motion.div>

        {/* Footer */}
        <motion.p
          className="text-white/30 text-xs mt-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          By playing, you agree to our Terms of Service
        </motion.p>
      </div>
    </motion.div>
  );
};

export default WelcomeOverlay;
